"use client";

import React, { createContext, useContext } from "react";

interface SubmitButtonProps {
  isSubmitting: boolean;
  disabled: boolean;
  formId: string;
}

interface TosCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  error: boolean;
}

export interface ElementsTheme {
  /** Stripe Elements appearance config */
  appearance: Record<string, unknown>;
  /** Stripe Elements font config */
  fonts?: Array<{ cssSrc: string }>;
  /** Render prop for the submit/pay button */
  renderSubmitButton: (props: SubmitButtonProps) => React.ReactNode;
  /** className for payment error alerts */
  errorClassName?: string;
  /** Optional render prop for loading skeleton */
  renderLoading?: () => React.ReactNode;
  /** Optional render prop for fetch-error state */
  renderError?: (error: string) => React.ReactNode;
  /** Optional render prop for the ToS checkbox. When provided, the checkout
   *  form requires acceptance before submission. */
  renderTosCheckbox?: (props: TosCheckboxProps) => React.ReactNode;
  /** When true, the checkout form shows a promo-code field (PE-1538). Injected
   *  by the app (brand + Statsig gate) so the package stays brand-blind. */
  enablePromoCodes?: boolean;
}

const ElementsThemeContext = createContext<ElementsTheme | null>(null);

export function useElementsTheme(): ElementsTheme {
  const theme = useContext(ElementsThemeContext);
  if (!theme) {
    throw new Error(
      "useElementsTheme must be used within an ElementsThemeProvider"
    );
  }
  return theme;
}

export function ElementsThemeProvider({
  children,
  ...theme
}: ElementsTheme & { children: React.ReactNode }) {
  return (
    <ElementsThemeContext.Provider value={theme}>
      {children}
    </ElementsThemeContext.Provider>
  );
}
